import React from 'react';
import { motion } from 'framer-motion';
import { Clock, LogOut, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Splash modal displayed when an account is pending school approval.
 * Triggers when:
 *  - role === 'eleve' and is_approved === false
 *  - role === 'parent' and is_approved === false (optional)
 *
 * Renders a backdrop-blurred overlay above the page, blocking interactions
 * with the underlying content.
 */
const PendingAccountModal = ({ message, contactEmail = 'contact@theenglishspeakingcenter.fr' }) => {
  const { user, logout } = useAuth();

  const isPending = user && user.is_approved === false;
  if (!isPending) return null;

  const defaultMessage = "Votre compte est en cours d'activation auprès de l'école, merci de patienter.";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md"
      data-testid="pending-account-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 28 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 p-8 pb-12 text-center relative">
          <div className="absolute -right-4 -top-4 w-32 h-32 bg-white/20 rounded-full blur-2xl" />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="relative inline-flex w-20 h-20 mx-auto bg-white/30 backdrop-blur-sm rounded-full items-center justify-center shadow-lg"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            >
              <Clock className="h-10 w-10 text-white" strokeWidth={2} />
            </motion.div>
          </motion.div>
        </div>

        {/* Content */}
        <div className="p-8 -mt-6 text-center">
          <h2 className="text-2xl font-bold text-[#2C3C65] mb-3">
            Compte en attente de validation
          </h2>
          <p className="text-[#475569] leading-relaxed mb-6">
            {message || defaultMessage}
          </p>

          {/* Status pill */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-amber-50 border border-amber-200 rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
            </span>
            <span className="text-amber-800 text-xs font-semibold uppercase tracking-wide">
              En cours de traitement
            </span>
          </div>

          {/* Contact info */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-left">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">
              Une question ?
            </p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-[#285BBB] hover:underline"
            >
              <Mail className="h-4 w-4" />
              {contactEmail}
            </a>
          </div>

          {/* Logout button */}
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm text-[#6777A1] hover:text-rose-600 transition-colors"
            data-testid="pending-logout"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PendingAccountModal;
